import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/axios';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  FiPlus,
  FiSearch,
  FiEdit,
  FiTrash2,
  FiEye,
  FiTruck,
  FiCalendar,
  FiDollarSign,
  FiRefreshCw,
  FiClock,
} from 'react-icons/fi';

const Cars = () => {
  const { user } = useAuth();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: '',
    licensePlate: '',
    mileage: '',
    fuelType: 'Gasoline',
    color: '',
    dailyPrice: '',
    transmission: 'Automatic',
    seats: '',
    status: 'Available',
    features: [],
    notes: '',
  });

  useEffect(() => {

    fetchCars();
  }, [user]);

  const fetchCars = async () => {
    try {
      console.log('Fetching cars...');
      console.log('User role:', user?.role);
      console.log('Auth token exists:', !!localStorage.getItem('token'));

      const response = await api.get('/api/cars');
      console.log('Cars fetched successfully:', response.data);
      setCars(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching cars:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view cars.');
      } else {
        toast.error('Failed to fetch cars');
      }
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Convert string values to proper types
    const carData = {
      ...formData,
      year: parseInt(formData.year),
      mileage: parseInt(formData.mileage),
      dailyPrice: parseFloat(formData.dailyPrice),
      seats: parseInt(formData.seats)
    };

    // Validate required fields
    if (!carData.brand || !carData.model || !carData.year || !carData.licensePlate ||
      !carData.mileage || !carData.fuelType || !carData.color || !carData.dailyPrice ||
      !carData.transmission || !carData.seats) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Validate numeric fields
    if (isNaN(carData.year) || isNaN(carData.mileage) || isNaN(carData.dailyPrice) || isNaN(carData.seats)) {
      toast.error('Veuillez entrer des nombres valides pour l\'année, le kilométrage, le prix journalier et les sièges');
      return;
    }

    try {
      console.log('Sending car data:', carData);
      const response = await api.post('/api/cars', carData);
      console.log('Car created successfully:', response.data);

      toast.success('Voiture ajoutée avec succès');
      setShowAddModal(false);
      setFormData({
        brand: '',
        model: '',
        year: '',
        licensePlate: '',
        mileage: '',
        fuelType: 'Gasoline',
        color: '',
        dailyPrice: '',
        transmission: 'Automatic',
        seats: '',
        status: 'Available',
        features: [],
        notes: '',
      });
      fetchCars();
    } catch (error) {
      console.error('Error adding car:', error);
      if (error.response?.status === 401) {
        toast.error('Authentification requise. Veuillez vous reconnecter.');
      } else if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas la permission d\'ajouter des voitures. Rôle admin ou staff requis.');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Échec de l\'ajout de la voiture. Veuillez vérifier vos données et réessayer.');
      }
    }
  };

  const handleEdit = (car) => {
    // Prevent editing rented cars
    if (car.status === 'Rented') {
      toast.error('Impossible de modifier une voiture louée. Terminez d\'abord la location.');
      return;
    }

    setEditingCar(car);
    setFormData({
      brand: car.brand,
      model: car.model,
      year: car.year.toString(),
      licensePlate: car.licensePlate,
      mileage: car.mileage.toString(),
      fuelType: car.fuelType,
      color: car.color,
      dailyPrice: car.dailyPrice.toString(),
      transmission: car.transmission,
      seats: car.seats.toString(),
      status: car.status,
      features: car.features || [],
      notes: car.notes || '',
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    // Prevent updating rented cars
    if (editingCar?.status === 'Rented') {
      toast.error('Impossible de modifier une voiture louée. Terminez d\'abord la location.');
      return;
    }

    // Convert string values to proper types
    const carData = {
      ...formData,
      year: parseInt(formData.year),
      mileage: parseInt(formData.mileage),
      dailyPrice: parseFloat(formData.dailyPrice),
      seats: parseInt(formData.seats)
    };

    // Validate required fields
    if (!carData.brand || !carData.model || !carData.year || !carData.licensePlate ||
      !carData.mileage || !carData.fuelType || !carData.color || !carData.dailyPrice ||
      !carData.transmission || !carData.seats) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Validate numeric fields
    if (isNaN(carData.year) || isNaN(carData.mileage) || isNaN(carData.dailyPrice) || isNaN(carData.seats)) {
      toast.error('Veuillez entrer des nombres valides pour l\'année, le kilométrage, le prix journalier et les sièges');
      return;
    }

    try {
      console.log('Updating car data:', carData);
      const response = await api.put(`/api/cars/${editingCar._id}`, carData);
      console.log('Car updated successfully:', response.data);

      toast.success('Voiture mise à jour avec succès');
      setShowEditModal(false);
      setEditingCar(null);
      setFormData({
        brand: '',
        model: '',
        year: '',
        licensePlate: '',
        mileage: '',
        fuelType: 'Gasoline',
        color: '',
        dailyPrice: '',
        transmission: 'Automatic',
        seats: '',
        status: 'Available',
        features: [],
        notes: '',
      });
      fetchCars();
    } catch (error) {
      console.error('Error updating car:', error);
      if (error.response?.status === 401) {
        toast.error('Authentification requise. Veuillez vous reconnecter.');
      } else if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas la permission de modifier des voitures. Rôle admin ou staff requis.');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Échec de la mise à jour de la voiture. Veuillez vérifier vos données et réessayer.');
      }
    }
  };

  const handleDelete = async (id) => {
    console.log('Delete function called with car ID:', id);
    console.log('Current user role:', user?.role);

    // Find the car to check its status
    const car = cars.find(c => c._id === id);
    if (!car) {
      toast.error('Voiture non trouvée');
      return;
    }

    // Prevent deleting rented cars
    if (car.status === 'Rented') {
      toast.error('Impossible de supprimer une voiture louée. Terminez d\'abord la location.');
      return;
    }

    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette voiture ?')) {
      try {
        console.log('Attempting to delete car...');
        const response = await api.delete(`/api/cars/${id}`);
        console.log('Delete response:', response);
        toast.success('Voiture supprimée avec succès');
        fetchCars();
      } catch (error) {
        console.error('Error deleting car:', error);
        console.error('Error response:', error.response);
        if (error.response?.status === 401) {
          toast.error('Authentification requise. Veuillez vous reconnecter.');
        } else if (error.response?.status === 403) {
          toast.error('Vous n\'avez pas la permission de supprimer des voitures. Rôle admin requis.');
        } else {
          toast.error('Échec de la suppression de la voiture');
        }
      }
    }
  };

  const handleUpdateCarStatuses = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/reservations/update-car-statuses');
      toast.success(response.data.message);
      fetchCars(); // Refresh the cars list
    } catch (error) {
      console.error('Error updating car statuses:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to update car statuses.');
      } else {
        toast.error('Failed to update car statuses');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCarStats = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/cars/update-stats');
      toast.success(response.data.message);
      fetchCars(); // Refresh the cars list
    } catch (error) {
      console.error('Error updating car statistics:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to update car statistics.');
      } else {
        toast.error('Failed to update car statistics');
      }
    } finally {
      setLoading(false);
    }
  };



  const filteredCars = cars.filter((car) => {
    const matchesSearch = car.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
      car.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      car.licensePlate.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || car.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Rented':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Maintenance':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Out of Service':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  // Get car color for border styling
  const getCarBorderColor = (carColor) => {
    const colorMap = {
      'white': 'border-white',
      'black': 'border-black',
      'silver': 'border-gray-400',
      'gray': 'border-gray-600',
      'red': 'border-red-500',
      'blue': 'border-blue-500',
      'green': 'border-green-500',
      'yellow': 'border-yellow-400',
      'orange': 'border-orange-500',
      'purple': 'border-purple-500',
      'pink': 'border-pink-400',
      'brown': 'border-amber-700',
      'beige': 'border-amber-100',
      'gold': 'border-yellow-500',
      'navy': 'border-blue-800',
      'teal': 'border-teal-500',
      'cyan': 'border-cyan-500',
      'lime': 'border-lime-500',
      'emerald': 'border-emerald-500',
      'indigo': 'border-indigo-500',
      'violet': 'border-violet-500',
      'fuchsia': 'border-fuchsia-500',
      'rose': 'border-rose-500',
      'amber': 'border-amber-500',
      'sky': 'border-sky-500',
      'slate': 'border-slate-500',
      'zinc': 'border-zinc-500',
      'stone': 'border-stone-500',
      'neutral': 'border-neutral-500',
      'warmGray': 'border-amber-600',
      'coolGray': 'border-slate-600'
    };

    return colorMap[carColor] || 'border-gray-300';
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'Available': return 'Disponible';
      case 'Rented': return 'Loué';
      case 'Maintenance': return 'En maintenance';
      case 'Out of Service': return 'Hors service';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Voitures</h1>
          <p className="text-gray-600 dark:text-gray-400">Gérez votre flotte de véhicules</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full lg:w-auto">
          {(user?.role === 'admin' || user?.role === 'staff') && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary flex items-center gap-2 text-sm"
            >
              <FiPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Ajouter une voiture</span>
              <span className="sm:hidden">Ajouter</span>
            </button>
          )}
          {user?.role === 'admin' && (
            <>
              <button
                onClick={handleUpdateCarStatuses}
                disabled={loading}
                className="btn btn-secondary flex items-center gap-2 text-sm"
              >
                <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden md:inline">Mettre à jour les statuts</span>
                <span className="md:hidden">Statuts</span>
              </button>

              <button
                onClick={handleUpdateCarStats}
                disabled={loading}
                className="btn btn-secondary flex items-center gap-2 text-sm"
              >
                <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden md:inline">Mettre à jour les statistiques</span>
                <span className="md:hidden">Stats</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher des voitures..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="all">Tous les statuts</option>
              <option value="Available">Disponible</option>
              <option value="Rented">Loué</option>
              <option value="Maintenance">En maintenance</option>
              <option value="Out of Service">Hors service</option>
            </select>
          </div>
        </div>
      </div>

      {/* Cars Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCars.map((car) => (
          <div
            key={car._id}
            className={`card p-6 hover:shadow-lg transition-shadow relative border-l-4 ${getCarBorderColor(car.color)}`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {car.year} {car.brand} {car.model}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {car.licensePlate}
                </p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(car.status)}`}>
                {getStatusText(car.status)}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <FiTruck className="mr-2" />
                {car.mileage.toLocaleString()} km
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <FiDollarSign className="mr-2" />
                {car.dailyPrice} MAD/jour
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <FiCalendar className="mr-2" />
                {car.totalRentals} locations
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <div
                  className="w-4 h-4 rounded mr-2 border border-gray-300"
                  style={{
                    backgroundColor: car.color === 'white' ? '#ffffff' :
                      car.color === 'black' ? '#000000' :
                        car.color === 'silver' ? '#9ca3af' :
                          car.color === 'gray' ? '#4b5563' :
                            car.color === 'red' ? '#ef4444' :
                              car.color === 'blue' ? '#3b82f6' :
                                car.color === 'green' ? '#10b981' :
                                  car.color === 'yellow' ? '#fbbf24' :
                                    car.color === 'orange' ? '#f97316' :
                                      car.color === 'purple' ? '#8b5cf6' :
                                        car.color === 'pink' ? '#f472b6' :
                                          car.color === 'brown' ? '#a16207' :
                                            car.color === 'beige' ? '#fef3c7' :
                                              car.color === 'gold' ? '#eab308' :
                                                car.color === 'navy' ? '#1e40af' :
                                                  car.color === 'teal' ? '#14b8a6' :
                                                    car.color === 'cyan' ? '#06b6d4' :
                                                      car.color === 'lime' ? '#84cc16' :
                                                        car.color === 'emerald' ? '#10b981' :
                                                          car.color === 'indigo' ? '#6366f1' :
                                                            car.color === 'violet' ? '#8b5cf6' :
                                                              car.color === 'fuchsia' ? '#d946ef' :
                                                                car.color === 'rose' ? '#f43f5e' :
                                                                  car.color === 'amber' ? '#f59e0b' :
                                                                    car.color === 'sky' ? '#0ea5e9' :
                                                                      car.color === 'slate' ? '#64748b' :
                                                                        car.color === 'zinc' ? '#71717a' :
                                                                          car.color === 'stone' ? '#78716c' :
                                                                            car.color === 'neutral' ? '#737373' :
                                                                              car.color === 'warmGray' ? '#a16207' :
                                                                                car.color === 'coolGray' ? '#475569' : '#6b7280'
                  }}
                ></div>
                Couleur: {car.color}
              </div>
            </div>

            <div className="flex justify-between items-center">
              <Link
                to={`/cars/${car._id}`}
                className="btn btn-secondary flex items-center"
              >
                <FiEye className="mr-1" />
                Voir
              </Link>
              <div className="flex gap-2">
                {(user?.role === 'admin' || user?.role === 'staff') && (
                  car.status === 'Rented' ? (
                    <button
                      className="btn btn-secondary p-2 opacity-50 cursor-not-allowed"
                      title="Impossible de modifier une voiture louée"
                      disabled
                    >
                      <FiEdit className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEdit(car)}
                      className="btn btn-secondary p-2"
                      title="Modifier la voiture"
                    >
                      <FiEdit className="h-4 w-4" />
                    </button>
                  )
                )}
                {user?.role === 'admin' && (
                  car.status === 'Rented' ? (
                    <button
                      className="btn btn-danger p-2 opacity-50 cursor-not-allowed"
                      title="Impossible de supprimer une voiture louée"
                      disabled
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDelete(car._id)}
                      className="btn btn-danger p-2"
                      title="Supprimer la voiture"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  )
                )}
              </div>
            </div>
            {/* <svg xmlns="http://www.w3.org/2000/svg" width="250" height="179" fill="none" viewBox="0 0 250 179" style={{ position: 'absolute', top: 40, zIndex: 0, right: 11, opacity: 0.3, color: (car.color) }}>
              <path fill="currentcolor" d="M240.93 91.623a71.074 71.074 0 0 0-1.088-5.804 2.167 2.167 0 0 0-.403-.806c-.524-.645-1.894-2.418-15.397-.766-11.769 1.451-24.948 8.102-27.205 9.23l-11.286 1.773c-2.217.202-4.514.081-7.013-.362-1.531-.282-2.982-.444-4.474-.645-.121 0-.201-.08-.322-.08h-.04c-3.91-.565-7.658-.887-11.125-1.089h-.121c-4.07-.201-7.778-.242-11.124-.12h-.242a100.3 100.3 0 0 0-11.043 1.007h-.161c-1.451.201-2.781.443-3.91.645-4.474.886-7.98 4.433-8.786 8.826-.887 4.998-.323 9.311.322 12.011.806 3.628 4.03 6.288 7.819 6.449 5.119.202 9.351.323 12.898.323 10.922 0 15.84-1.048 22.087-3.386 1.411-.524 2.7-1.129 3.95-1.773.201-.041.403-.162.604-.283 4.837-2.499 8.424-5.683 10.601-9.512h6.448l5.603 2.983c3.99 2.136 8.464 2.942 12.656 2.338 7.577-1.008 15.275-3.023 20.354-4.555a14.75 14.75 0 0 0 10.398-16.484v.08Zm-102.939 26.279c-.806 0-1.612-.081-2.499-.081-1.934-.08-3.587-1.41-3.99-3.264a27.403 27.403 0 0 1-.282-10.399c.484-2.741 2.7-4.958 5.522-5.522.363-.08.806-.161 1.209-.242v19.468l.04.04Zm11.205.242c-2.096 0-4.433 0-7.134-.081V97.79a79.321 79.321 0 0 1 7.134-.645v20.999Zm11.285-.806c-2.055.362-4.393.564-7.174.685v-21.08h2.459c1.491 0 3.063 0 4.715.081v20.314Zm11.205-3.507c-.886.403-1.813.806-2.821 1.209a48.516 48.516 0 0 1-4.313 1.411V97.306c2.298.161 4.635.363 7.134.685v15.84Zm10.681-9.472c-1.29 2.701-3.547 5.119-6.61 7.134V98.555c.685.121 1.33.202 2.015.323 2.056.403 4.031.605 5.965.564-.08 1.209-.403 2.983-1.37 4.917Zm46.955-.201c-4.957 1.491-12.414 3.426-19.749 4.393-3.345.524-6.973-.161-10.157-1.894l-6.046-3.225a1.91 1.91 0 0 0-.967-.241h-5.199c.483-1.653.645-3.104.645-4.192l9.915-1.532c.241 0 .443-.12.644-.201.121-.08 14.228-7.497 26.078-8.948 7.255-.887 10.358-.685 11.527-.484.202.968.524 2.58.806 4.434.847 5.28-2.378 10.399-7.497 11.89Zm-.765 26.883a3.305 3.305 0 0 0-3.749.847c-2.176 2.378-4.232 4.03-5.925 4.796-2.337 1.048-14.59 3.668-32.002 6.812-1.169.201-2.136 1.007-2.579 2.136a3.414 3.414 0 0 0 .483 3.264c.726.927 1.895 2.62 3.507 5.442 3.184 5.602 7.416 6.61 11.567 6.61 1.169 0 2.338-.081 3.507-.162 2.297-.161 6.973-.685 13.946-1.612 6.005-.806 10.801-5.32 11.93-11.245.766-3.99 1.249-8.585 1.451-13.583a3.322 3.322 0 0 0-2.136-3.264v-.041Zm-3.346 16.122a9.887 9.887 0 0 1-8.464 7.94c-6.771.887-11.527 1.451-13.744 1.613-4.998.362-8.343.564-11.164-4.394-1.088-1.934-2.136-3.627-3.104-4.957 10.842-1.975 28.254-5.28 31.801-6.933 1.854-.846 3.829-2.337 5.925-4.393-.202 4.071-.645 7.819-1.29 11.124h.04ZM75.196 116.814s.363.201.605.282c1.25.604 2.539 1.249 3.95 1.773 6.247 2.338 11.164 3.386 22.087 3.386 3.587 0 7.779-.121 12.938-.323 3.788-.161 7.013-2.821 7.819-6.448a31.666 31.666 0 0 0 .322-12.052c-.766-4.353-4.312-7.9-8.786-8.786a85.697 85.697 0 0 0-3.87-.645h-.161c-2.982-.443-6.69-.806-11.043-1.008h-.242a115.884 115.884 0 0 0-11.124.121h-.121c-3.466.202-7.175.565-11.084 1.089h-.04c-.121 0-.202.04-.323.08a246.25 246.25 0 0 0-4.514.645c-2.499.484-4.756.605-6.852.403l-11.406-1.773c-2.257-1.17-15.437-7.78-27.206-9.23-13.502-1.653-14.873.12-15.397.766a2.168 2.168 0 0 0-.403.806s-.604 2.499-1.088 5.804c-1.169 7.335 3.265 14.429 10.399 16.484 5.119 1.532 12.776 3.547 20.354 4.555 4.192.604 8.706-.202 12.696-2.338l5.602-2.982h6.409c2.176 3.829 5.763 7.013 10.6 9.512l-.12-.121Zm36.879-18.38c.403.081.847.162 1.209.202 2.822.564 5.038 2.781 5.522 5.482a26.047 26.047 0 0 1-.282 10.439c-.403 1.813-2.056 3.143-3.99 3.224-.887 0-1.693.081-2.499.081V98.394l.04.04Zm-11.205-1.29c2.661.162 5.039.404 7.134.646v20.233c-2.66.08-4.997.08-7.134.08V97.105v.04Zm-11.245-.08c2.54-.08 4.958-.12 7.175-.08v21.079c-2.781-.121-5.12-.363-7.175-.685V97.065Zm-11.204.927a118.88 118.88 0 0 1 7.133-.725v19.185c-1.45-.403-2.82-.887-4.312-1.411a29.588 29.588 0 0 1-2.822-1.209v-15.84Zm-20.717 5.2c-.323 0-.645.08-.968.241l-6.086 3.225c-3.184 1.693-6.811 2.418-10.197 1.894-7.295-.967-14.751-2.942-19.75-4.393a10.665 10.665 0 0 1-7.496-11.93 80.63 80.63 0 0 1 .806-4.394c1.169-.201 4.272-.403 11.527.484 11.89 1.491 25.957 8.907 26.078 8.948.201.12.403.161.645.201L62.178 99c0 1.088.201 2.539.645 4.192h-5.2.08Zm9.995 1.168c-.967-1.934-1.25-3.667-1.37-4.917 1.975.04 3.87-.161 5.925-.564a34.68 34.68 0 0 1 2.015-.282v12.938c-3.023-2.056-5.28-4.434-6.61-7.175h.04Zm2.5 52.881c3.425 1.209 15.678 3.99 54.854 3.99 39.177 0 51.43-2.781 54.855-3.99a2.034 2.034 0 0 0 1.29-2.459c-.121-.484-3.063-11.406-10.802-13.502-6.851-1.854-43.811-1.975-45.383-1.975-1.572 0-38.532.121-45.383 1.975-7.699 2.096-10.681 13.018-10.802 13.502a2.034 2.034 0 0 0 1.29 2.459h.08Zm10.56-12.011c5.239-1.451 33.734-1.814 44.294-1.854 10.56 0 39.056.403 44.296 1.854 3.627 1.007 6.086 5.602 7.254 8.665-4.876 1.169-18.177 3.225-51.55 3.225-33.372 0-46.673-2.056-51.55-3.225 1.17-3.063 3.628-7.698 7.296-8.665h-.04Zm-17.614-1.733c-17.412-3.144-29.624-5.764-31.962-6.812-1.733-.806-3.789-2.459-5.965-4.837a3.277 3.277 0 0 0-3.748-.806c-1.33.565-2.177 1.854-2.096 3.305.201 4.998.645 9.593 1.41 13.583a13.998 13.998 0 0 0 11.93 11.245c6.973.927 11.649 1.451 14.027 1.612 1.128.081 2.297.162 3.466.162 4.151 0 8.383-1.008 11.527-6.61 1.612-2.822 2.781-4.515 3.507-5.442a3.283 3.283 0 0 0 .483-3.264c-.443-1.129-1.37-1.895-2.58-2.136Zm-4.998 8.786c-2.821 4.958-6.126 4.716-11.205 4.393-2.176-.161-6.892-.685-13.663-1.612-4.272-.564-7.658-3.748-8.464-7.94-.645-3.305-1.048-7.053-1.29-11.124 2.096 2.055 4.07 3.547 5.925 4.393 3.587 1.612 20.958 4.958 31.8 6.933-.967 1.33-2.015 3.022-3.103 4.957ZM82.451 51.439l10.238-.08c1.128 0 2.055-.928 2.015-2.056 0-1.129-1.048-2.015-2.056-2.015l-10.237.08c-1.129 0-2.056.927-2.016 2.056 0 1.128.928 2.015 2.056 2.015Z" />
              <path fill="currentcolor" d="M153.307 164.696c-8.343.403-17.855.605-28.254.605-49.655 0-57.515-4.394-58.643-5.401a1.853 1.853 0 0 0-.686-1.209 2.076 2.076 0 0 0-2.337-.283L53.23 163.93a10.725 10.725 0 0 1-6.368 1.29l-6.167-.685h-.08l-10.077-1.088a15.843 15.843 0 0 1-14.147-14.148l-.645-5.521-.604-5.522a2.031 2.031 0 0 0-1.935-1.814h-.04c-.283 0-.524 0-.766.121-.04 0-.08.04-.121.04-.161.081-.322.121-.443.242a2.017 2.017 0 0 0-.766 1.25c-.564 2.781-.847 6.247-.927 10.439v2.418c0 4.272.201 7.94.322 9.794l1.975-.121-.685.121-1.773.323-1.21-.323L7 160.262c-3.426-30.752-2.983-40.627-2.499-52.033.08-1.895.161-3.829.242-5.925v-1.532c.161-4.514.282-9.673 3.668-17.492 3.063-6.933 18.298-18.016 23.94-21.724.29-.195.525-.458.686-.766.04-.08 4.635-8.666 10.52-18.661 7.577-12.697 13.38-21.362 17.814-26.521l4.112-.242c-6.61 9.35-15.316 23.7-21.322 34.058a2.155 2.155 0 0 0 0 2.096c.403.644 1.048 1.047 1.854.967 5.562-.323 12.656-.605 21.08-.766 1.128 0 2.015-.967 2.015-2.096 0-1.128-.927-2.015-2.056-2.015h-.04c-6.65.161-12.494.322-17.412.564 6.45-10.963 15.115-24.989 21.12-33.09.403 0 .806 0 1.21-.04l26.681-.806c15.115-.283 26.521-.283 26.521-.283s6.368 0 15.88.121h3.426l4.595.121h.322c4.273.08 8.908.202 13.704.323 2.257.08 4.554.12 6.892.201h1.088l2.136.121c2.137.08 4.273.161 6.409.282 5.844 7.98 14.55 21.966 21.16 33.13-28.213-1.37-75.128-.966-75.572-.966 0 0-6.852-.081-16.968-.081a2.064 2.064 0 0 0-2.056 2.056c0 1.128.927 2.055 2.056 2.055 10.076 0 16.888.08 16.968.08.524 0 51.752-.483 79.159 1.13h.121c.726 0 1.371-.364 1.733-.968a1.968 1.968 0 0 0 0-2.096c-6.328-10.882-14.953-24.909-21.402-34.098l4.071.242c4.393 5.159 10.238 13.824 17.815 26.52a533.158 533.158 0 0 1 10.52 18.662c.161.322.403.564.685.766 5.602 3.667 20.878 14.751 23.941 21.684 3.426 7.819 3.547 12.978 3.627 17.492v1.532c.081 2.136.202 4.151.282 6.086l.121 2.418c.041 1.128.887 2.096 2.137 1.935 1.128-.041 2.015-1.008 1.934-2.137l-.121-2.418c-.08-1.935-.201-3.869-.242-6.046v-1.491c-.161-4.675-.282-10.48-4.03-19.064-3.869-8.827-21.845-21.08-24.989-23.176-.645-1.168-2.418-4.473-4.877-8.826.524-.887 1.894-1.089 3.063-1.048 3.305.16 6.247.12 11.608-.484 10.439-1.21 11.205-8.424 11.205-8.907v-5.482c0-1.531-.726-2.942-1.975-3.748-6.933-4.595-17.896-4.071-22.289-3.628-1.934.202-3.547 1.612-4.03 3.547l-1.25 5.24s-.806 2.902-2.579 3.748c-7.578-12.656-13.502-21.361-17.976-26.44-.041-.04-.081-.12-.121-.161-.927-1.048-1.854-2.015-2.701-2.781C177.087-1.08 127.149.01 125.134.05 123.038.01 73.02-1.08 61.573 9.4c-.846.766-1.773 1.733-2.7 2.78-.04.041-.081.122-.121.162-4.474 5.079-10.359 13.784-17.976 26.44-1.774-.846-2.58-3.708-2.58-3.708l-1.29-5.28a4.635 4.635 0 0 0-4.03-3.547c-4.393-.443-15.316-.967-22.289 3.587-1.209.807-1.975 2.217-1.975 3.749v5.642c0 .323.766 7.538 11.205 8.706 5.36.605 8.343.645 11.608.484 1.169 0 2.54.121 3.063 1.048-2.459 4.353-4.272 7.658-4.877 8.827-3.144 2.096-21.12 14.389-24.989 23.175C.874 90.051.752 95.856.632 100.53v1.492c-.121 2.055-.202 3.99-.282 5.884-.524 11.729-.968 21.886 2.66 54.049.12 3.104 1.128 16.566 8.02 16.566h23.942c.362 0 3.627-.162 5.844-2.983 1.37-1.733 2.015-3.99 1.894-6.771l3.668.403a14.886 14.886 0 0 0 8.786-1.774l8.465-4.635c5.118 4.273 25.754 6.449 61.384 6.449 10.479 0 20.072-.201 28.455-.604 1.129-.041 2.016-1.008 1.935-2.137-.04-1.128-.887-2.136-2.136-1.934l.04.161Zm62.634-128.654 1.25-5.28a.557.557 0 0 1 .443-.443c3.95-.403 13.744-.927 19.749 3.305v5.28s-.604 4.232-7.577 5.038c-5.119.605-7.819.605-10.963.484-2.257-.121-4.03.443-5.361 1.45-.644-1.128-1.33-2.296-2.015-3.506 2.58-1.41 3.99-4.353 4.474-6.287v-.04Zm-90.847-31.88c13.22-.364 48.003.402 59.046 7.053-1.128-.04-2.257-.121-3.385-.162h-.081c-1.612-.08-3.224-.16-4.796-.201a193.69 193.69 0 0 0-4.837-.161l-3.547-.121c-.886 0-1.692-.162-2.579-.08-.726 0-1.451 0-2.177-.041h-.282c-1.168 0-2.337-.08-3.466-.08-1.411 0-2.741-.081-4.111-.081h-1.249a88.95 88.95 0 0 1-3.346-.081h-1.652c-13.14-.242-22.934-.242-23.538-.242-.645 0-11.205 0-25.15.282h-1.532c-8.585.162-18.339.444-27.81.887h-1.089s-.04.04-.08.04c-1.13.04-2.298.121-3.386.162 10.963-6.65 45.827-7.417 59.127-7.094l-.08-.08Zm-93.87 40.224c-3.145.16-5.845.12-10.964-.484-6.972-.806-7.577-4.837-7.577-4.837l.12-5.844c5.845-3.87 15.68-3.345 19.59-2.942.241 0 .402.201.443.443l1.29 5.28c.483 1.935 1.894 4.877 4.473 6.288a134.386 134.386 0 0 1-2.055 3.506c-1.33-1.007-3.144-1.572-5.32-1.45v.04Zm6.408 128.653c-1.088 1.371-2.781 1.451-2.74 1.491H11.03c-1.612 0-3.143-4.876-3.708-9.955l.686.161.927.242c1.41.323 2.861 0 3.95-.887a4.436 4.436 0 0 0 1.612-3.708c-.08-1.289-.162-2.539-.202-3.788a19.987 19.987 0 0 0 15.76 10.882l8.504.927c.201 2.015-.121 3.587-.927 4.635Zm210.271-45.545c-1.25 0-2.096.847-2.136 1.975-.323 7.941-1.169 17.735-2.62 30.753l-1.814.484-1.169.322.484 1.854-.605-1.41-.362-.806c.121-1.814.322-5.442.322-9.795 0-5.36-.322-9.552-.967-12.857-.04-.282-.161-.484-.282-.725-.363-.605-1.008-.968-1.774-.927-.766.04 0 0-.04 0-1.008.04-1.814.806-1.935 1.813l-.604 5.603-.605 5.441a15.88 15.88 0 0 1-14.147 14.147l-10.076 1.088h-.081l-6.166.685a10.608 10.608 0 0 1-6.369-1.289l-10.156-5.522a2.007 2.007 0 0 0-2.338.282 2.16 2.16 0 0 0-.685 1.209c-.645.524-3.708 2.378-16.445 3.708a2.042 2.042 0 0 0-1.813 2.257c.12 1.129 1.209 1.935 2.257 1.814 12.534-1.33 16.968-3.184 18.741-4.756l8.464 4.595a14.905 14.905 0 0 0 7.215 1.854c.524 0 1.088 0 1.612-.081l3.668-.403c-.121 2.781.524 5.038 1.894 6.771 2.257 2.781 5.482 2.943 5.925 2.983h23.861c6.892 0 7.9-13.422 8.02-16.525 1.572-13.825 2.459-24.143 2.822-32.446a2.069 2.069 0 0 0-1.975-2.136l-.121.04Zm-8.867 47.036h-23.78s-1.774-.12-2.821-1.491c-.807-1.007-1.129-2.62-.927-4.635l8.504-.927a19.905 19.905 0 0 0 15.759-10.882c-.08 1.612-.161 2.942-.201 3.829a4.783 4.783 0 0 0 1.652 3.708c1.129.927 2.539 1.249 3.99.886l1.048-.282.524-.121c-.564 5.079-2.096 9.956-3.708 9.956l-.04-.041Z" />
            </svg> */}


          </div>
        ))}
      </div>

      {filteredCars.length === 0 && (
        <div className="text-center py-12">
          <FiTruck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Aucune voiture trouvée</p>
        </div>
      )}

      {/* Add Car Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Ajouter une nouvelle voiture</h2>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Marque
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Modèle
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Année
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Plaque d'immatriculation
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.licensePlate}
                      onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Kilométrage
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.mileage}
                      onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Prix journalier
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.dailyPrice}
                      onChange={(e) => setFormData({ ...formData, dailyPrice: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Type de carburant
                    </label>
                    <select
                      value={formData.fuelType}
                      onChange={(e) => setFormData({ ...formData, fuelType: e.target.value })}
                      className="input"
                    >
                      <option value="Gasoline">Essence</option>
                      <option value="Diesel">Diesel</option>
                      <option value="Electric">Électrique</option>
                      <option value="Hybrid">Hybride</option>
                      <option value="Plug-in Hybrid">Hybride rechargeable</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Couleur
                    </label>
                    <select
                      required
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="input"
                    >
                      <option value="">Sélectionnez une couleur</option>
                      <option value="white">Blanc</option>
                      <option value="black">Noir</option>
                      <option value="silver">Argent</option>
                      <option value="gray">Gris</option>
                      <option value="red">Rouge</option>
                      <option value="blue">Bleu</option>
                      <option value="green">Vert</option>
                      <option value="yellow">Jaune</option>
                      <option value="orange">Orange</option>
                      <option value="purple">Violet</option>
                      <option value="pink">Rose</option>
                      <option value="brown">Marron</option>
                      <option value="beige">Beige</option>
                      <option value="gold">Or</option>
                      <option value="navy">Bleu marine</option>
                      <option value="teal">Bleu-vert</option>
                      <option value="cyan">Cyan</option>
                      <option value="lime">Lime</option>
                      <option value="emerald">Émeraude</option>
                      <option value="indigo">Indigo</option>
                      <option value="violet">Violet foncé</option>
                      <option value="fuchsia">Fuchsia</option>
                      <option value="rose">Rose foncé</option>
                      <option value="amber">Ambre</option>
                      <option value="sky">Bleu ciel</option>
                      <option value="slate">Ardoise</option>
                      <option value="zinc">Zinc</option>
                      <option value="stone">Pierre</option>
                      <option value="neutral">Neutre</option>
                      <option value="warmGray">Gris chaud</option>
                      <option value="coolGray">Gris froid</option>
                    </select>

                    {/* Color Preview */}
                    {formData.color && (
                      <div className="mt-2 flex items-center space-x-2">
                        <div
                          className={`w-6 h-6 rounded border ${formData.color === 'white' ? 'border-gray-300' : 'border-transparent'
                            }`}
                          style={{
                            backgroundColor: formData.color === 'white' ? '#ffffff' :
                              formData.color === 'black' ? '#000000' :
                                formData.color === 'silver' ? '#9ca3af' :
                                  formData.color === 'gray' ? '#4b5563' :
                                    formData.color === 'red' ? '#ef4444' :
                                      formData.color === 'blue' ? '#3b82f6' :
                                        formData.color === 'green' ? '#10b981' :
                                          formData.color === 'yellow' ? '#fbbf24' :
                                            formData.color === 'orange' ? '#f97316' :
                                              formData.color === 'purple' ? '#8b5cf6' :
                                                formData.color === 'pink' ? '#f472b6' :
                                                  formData.color === 'brown' ? '#a16207' :
                                                    formData.color === 'beige' ? '#fef3c7' :
                                                      formData.color === 'gold' ? '#eab308' :
                                                        formData.color === 'navy' ? '#1e40af' :
                                                          formData.color === 'teal' ? '#14b8a6' :
                                                            formData.color === 'cyan' ? '#06b6d4' :
                                                              formData.color === 'lime' ? '#84cc16' :
                                                                formData.color === 'emerald' ? '#10b981' :
                                                                  formData.color === 'indigo' ? '#6366f1' :
                                                                    formData.color === 'violet' ? '#8b5cf6' :
                                                                      formData.color === 'fuchsia' ? '#d946ef' :
                                                                        formData.color === 'rose' ? '#f43f5e' :
                                                                          formData.color === 'amber' ? '#f59e0b' :
                                                                            formData.color === 'sky' ? '#0ea5e9' :
                                                                              formData.color === 'slate' ? '#64748b' :
                                                                                formData.color === 'zinc' ? '#71717a' :
                                                                                  formData.color === 'stone' ? '#78716c' :
                                                                                    formData.color === 'neutral' ? '#737373' :
                                                                                      formData.color === 'warmGray' ? '#a16207' :
                                                                                        formData.color === 'coolGray' ? '#475569' : '#6b7280'
                          }}
                        ></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Couleur sélectionnée: {formData.color}
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Sièges
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.seats}
                      onChange={(e) => setFormData({ ...formData, seats: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Transmission
                    </label>
                    <select
                      required
                      value={formData.transmission}
                      onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                      className="input"
                    >
                      <option value="Automatic">Automatique</option>
                      <option value="Manual">Manuelle</option>
                      <option value="CVT">CVT</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Statut
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="input"
                    >
                      <option value="Available">Disponible</option>
                      <option value="Maintenance">En maintenance</option>
                      <option value="Out of Service">Hors service</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Équipements (séparés par des virgules)
                    </label>
                    <input
                      type="text"
                      placeholder="GPS, Bluetooth, Caméra de recul"
                      value={formData.features.join(', ')}
                      onChange={(e) => setFormData({
                        ...formData,
                        features: e.target.value.split(',').map(f => f.trim()).filter(f => f)
                      })}
                      className="input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes
                  </label>
                  <textarea
                    placeholder="Notes supplémentaires sur la voiture..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="input"
                    rows="3"
                  />
                </div>
              </form>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({
                      brand: '',
                      model: '',
                      year: '',
                      licensePlate: '',
                      mileage: '',
                      fuelType: 'Gasoline',
                      color: '',
                      dailyPrice: '',
                      transmission: 'Automatic',
                      seats: '',
                      status: 'Available',
                      features: [],
                      notes: '',
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="btn btn-primary"
                >
                  Ajouter la voiture
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de modification de voiture */}
      {showEditModal && editingCar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Modifier la voiture</h2>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <form onSubmit={handleUpdate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Marque
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Modèle
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Année
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>



                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Plaque d'immatriculation
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.licensePlate}
                      onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Kilométrage
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.mileage}
                      onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Prix journalier
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.dailyPrice}
                      onChange={(e) => setFormData({ ...formData, dailyPrice: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Type de carburant
                    </label>
                    <select
                      value={formData.fuelType}
                      onChange={(e) => setFormData({ ...formData, fuelType: e.target.value })}
                      className="input"
                    >
                      <option value="Gasoline">Gasoline</option>
                      <option value="Diesel">Diesel</option>
                      <option value="Electric">Electric</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="Plug-in Hybrid">Plug-in Hybrid</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Couleur
                    </label>
                    <select
                      required
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="input"
                    >
                      <option value="">Sélectionnez une couleur</option>
                      <option value="white" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-white border border-gray-300 rounded mr-2"></span>
                        Blanc
                      </option>
                      <option value="black" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-black rounded mr-2"></span>
                        Noir
                      </option>
                      <option value="silver" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-gray-400 rounded mr-2"></span>
                        Argent
                      </option>
                      <option value="gray" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-gray-600 rounded mr-2"></span>
                        Gris
                      </option>
                      <option value="red" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-red-500 rounded mr-2"></span>
                        Rouge
                      </option>
                      <option value="blue" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-blue-500 rounded mr-2"></span>
                        Bleu
                      </option>
                      <option value="green" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-green-500 rounded mr-2"></span>
                        Vert
                      </option>
                      <option value="yellow" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-yellow-400 rounded mr-2"></span>
                        Jaune
                      </option>
                      <option value="orange" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-orange-500 rounded mr-2"></span>
                        Orange
                      </option>
                      <option value="purple" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-purple-500 rounded mr-2"></span>
                        Violet
                      </option>
                      <option value="pink" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-pink-400 rounded mr-2"></span>
                        Rose
                      </option>
                      <option value="brown" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-amber-700 rounded mr-2"></span>
                        Marron
                      </option>
                      <option value="beige" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-amber-100 border border-gray-300 rounded mr-2"></span>
                        Beige
                      </option>
                      <option value="gold" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-yellow-500 rounded mr-2"></span>
                        Or
                      </option>
                      <option value="navy" className="flex items-center">
                        <span className="inline-block w-4 h-4 bg-blue-800 rounded mr-2"></span>
                        Bleu marine
                      </option>
                    </select>

                    {/* Color Preview */}
                    {formData.color && (
                      <div className="mt-2 flex items-center space-x-2">
                        <div
                          className={`w-6 h-6 rounded border ${formData.color === 'white' ? 'border-gray-300' : 'border-transparent'
                            }`}
                          style={{
                            backgroundColor: formData.color === 'white' ? '#ffffff' :
                              formData.color === 'black' ? '#000000' :
                                formData.color === 'silver' ? '#9ca3af' :
                                  formData.color === 'gray' ? '#4b5563' :
                                    formData.color === 'red' ? '#ef4444' :
                                      formData.color === 'blue' ? '#3b82f6' :
                                        formData.color === 'green' ? '#10b981' :
                                          formData.color === 'yellow' ? '#fbbf24' :
                                            formData.color === 'orange' ? '#f97316' :
                                              formData.color === 'purple' ? '#8b5cf6' :
                                                formData.color === 'pink' ? '#f472b6' :
                                                  formData.color === 'brown' ? '#a16207' :
                                                    formData.color === 'beige' ? '#fef3c7' :
                                                      formData.color === 'gold' ? '#eab308' :
                                                        formData.color === 'navy' ? '#1e40af' :
                                                          formData.color === 'teal' ? '#14b8a6' :
                                                            formData.color === 'cyan' ? '#06b6d4' :
                                                              formData.color === 'lime' ? '#84cc16' :
                                                                formData.color === 'emerald' ? '#10b981' :
                                                                  formData.color === 'indigo' ? '#6366f1' :
                                                                    formData.color === 'violet' ? '#8b5cf6' :
                                                                      formData.color === 'fuchsia' ? '#d946ef' :
                                                                        formData.color === 'rose' ? '#f43f5e' :
                                                                          formData.color === 'amber' ? '#f59e0b' :
                                                                            formData.color === 'sky' ? '#0ea5e9' :
                                                                              formData.color === 'slate' ? '#64748b' :
                                                                                formData.color === 'zinc' ? '#71717a' :
                                                                                  formData.color === 'stone' ? '#78716c' :
                                                                                    formData.color === 'neutral' ? '#737373' :
                                                                                      formData.color === 'warmGray' ? '#a16207' :
                                                                                        formData.color === 'coolGray' ? '#475569' : '#6b7280'
                          }}
                        ></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Couleur sélectionnée: {formData.color}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Transmission
                    </label>
                    <select
                      required
                      value={formData.transmission}
                      onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                      className="input"
                    >
                      <option value="Automatic">Automatique</option>
                      <option value="Manual">Manuelle</option>
                      <option value="CVT">CVT</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Sièges
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.seats}
                      onChange={(e) => setFormData({ ...formData, seats: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Statut
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className={`input ${editingCar?.status === 'Rented' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={editingCar?.status === 'Rented'}
                    >
                      <option value="Available">Disponible</option>
                      <option value="Maintenance">En maintenance</option>
                      <option value="Out of Service">Hors service</option>
                      {editingCar?.status === 'Rented' && (
                        <option value="Rented" disabled>Loué (non modifiable)</option>
                      )}
                    </select>
                    {editingCar?.status === 'Rented' && (
                      <p className="text-xs text-orange-600 mt-1">
                        ⚠️ Le statut ne peut pas être modifié tant que la voiture est louée
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes
                  </label>
                  <textarea
                    placeholder="Notes supplémentaires sur la voiture..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="input"
                    rows="3"
                  />
                </div>
              </form>
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingCar(null);
                    setFormData({
                      brand: '',
                      model: '',
                      year: '',
                      licensePlate: '',
                      mileage: '',
                      fuelType: 'Gasoline',
                      color: '',
                      dailyPrice: '',
                      transmission: 'Automatic',
                      seats: '',
                      status: 'Available',
                      features: [],
                      notes: '',
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleUpdate}
                  className="btn btn-primary"
                >
                  Mettre à jour
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cars;